import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useCare } from '../contexts/CareContext'
import LoadingSpinner from '../components/shared/LoadingSpinner'
import EmptyState from '../components/shared/EmptyState'
import RequestCallModal from '../components/shared/RequestCallModal'
import CarePlanActionsList from '../components/care-plan/CarePlanActionsList'
import GoalProgressList from '../components/care-plan/GoalProgressList'
import { parseGoals, trackingChips } from '../lib/carePlan'
import { buildDomains, TONE_COLOR, statusFromPct } from '../lib/functional'

const TABS = [
  ['goals', 'Goals'],
  ['plan', 'Care Plan'],
  ['progress', 'Progress'],
]

const PRIO_LABEL = { high: 'High priority', medium: 'Medium priority', ongoing: 'Ongoing' }

export default function GoalsPage() {
  const { recipient } = useCare()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [plan, setPlan] = useState(null)
  const [assessment, setAssessment] = useState(null)
  const [callModal, setCallModal] = useState(null)
  const [tab, setTab] = useState('goals')

  const load = useCallback(async () => {
    if (!recipient) return
    setLoading(true)
    const [planRes, assessRes] = await Promise.all([
      supabase
        .from('care_plans')
        .select('*')
        .eq('care_recipient_id', recipient.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle(),
      supabase
        .from('functional_assessments')
        .select('*')
        .eq('care_recipient_id', recipient.id)
        .order('assessment_date', { ascending: false })
        .limit(1)
        .maybeSingle(),
    ])
    setPlan(planRes.data || null)
    setAssessment(assessRes.data || null)
    setLoading(false)
  }, [recipient])

  useEffect(() => { load() }, [load])

  if (loading) return <LoadingSpinner label="Loading goals…" />

  const goals = parseGoals(plan?.current_priorities)
  // Goals grid shows the four domains families ask about most.
  const domains = buildDomains(recipient, assessment).filter((d) =>
    ['mobility', 'memory', 'hearing', 'vision'].includes(d.key)
  )

  const name = recipient?.preferred_name || recipient?.full_name || 'them'
  const lastUpdated = plan?.updated_at
    ? new Date(plan.updated_at).toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
      })
    : null

  return (
    <div className="stack">
      <h1 className="title">Goals</h1>

      <div className="segmented">
        {TABS.map(([v, l]) => (
          <button key={v} className={tab === v ? 'on' : ''} onClick={() => setTab(v)}>
            {l}
          </button>
        ))}
      </div>

      {tab === 'plan' && <CarePlanActionsList />}

      {tab === 'progress' && <GoalProgressList />}

      {tab === 'goals' && (
      <>
      <div>
        <p className="subtitle" style={{ lineHeight: 1.55 }}>
          These are {name}'s care goals. Review and update monthly — or after
          any major health change.
        </p>
        {lastUpdated && (
          <div className="muted mt-4" style={{ fontSize: 12 }}>
            Last updated {lastUpdated}
          </div>
        )}
      </div>

      {/* ── Part A — care goals ─────────────────────────────── */}
      <div>
        <div className="section-title" style={{ marginBottom: 8 }}>Care goals</div>

        {goals.length === 0 ? (
          <EmptyState
            icon="🎯"
            title="No care plan yet"
            message="Ginie can build a personalised plan with goals, risk flags, and daily actions in about five minutes."
            action={
              <button type="button" className="btn" onClick={() => setCallModal('unsure')}>
                Generate care plan →
              </button>
            }
          />
        ) : (
          <div className="stack" style={{ gap: 10 }}>
            {goals.map((g) => {
              const chips = trackingChips(g)
              return (
                <div key={g.id} className={`card tint-${g.priority}`}>
                  <div className="row" style={{ gap: 8 }}>
                    <span className={`dot dot-${g.priority}`} aria-hidden />
                    <span className={`prio-label prio-${g.priority}`}>
                      {PRIO_LABEL[g.priority]}
                    </span>
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 16, marginTop: 6 }}>{g.title}</div>
                  {g.description && (
                    <p style={{ fontSize: 14, marginTop: 4, lineHeight: 1.55 }}>{g.description}</p>
                  )}
                  {chips.length > 0 && (
                    <div className="row wrap" style={{ gap: 6, marginTop: 10 }}>
                      {chips.map((c) => (
                        <span
                          key={c}
                          className="badge"
                          style={{ background: 'rgba(255,255,255,0.7)', color: 'var(--teal-dark)' }}
                        >
                          {c}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {goals.length > 0 && (
          <button
            type="button"
            className="mt-16"
            onClick={() => setTab('plan')}
            style={{ background: 'none', border: 'none', padding: 0, color: 'var(--pa)', fontWeight: 600, fontSize: 14, textAlign: 'left' }}
          >
            See care plan actions for each goal →
          </button>
        )}
      </div>

      {/* ── Part B — functional status ──────────────────────── */}
      <div>
        <div className="section-title" style={{ marginBottom: 8 }}>Functional status</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {domains.map((d) => {
            const s = statusFromPct(d.pct)
            const color = TONE_COLOR[d.tone] || 'var(--muted)'
            return (
              <div key={d.key} className="card" style={{ padding: 14 }}>
                <div className="row" style={{ gap: 8 }}>
                  <span style={{ fontSize: 20, lineHeight: 1 }} aria-hidden>{d.emoji}</span>
                  <span style={{ fontWeight: 700, fontSize: 15 }}>{d.name}</span>
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color, marginTop: 6 }}>
                  {d.pct == null ? 'Not assessed' : `${s.label} ${s.arrow}`}
                </div>
                <div className="meter" style={{ marginTop: 8 }}>
                  <span style={{ width: `${d.pct ?? 0}%`, background: color }} />
                </div>
              </div>
            )
          })}
        </div>
        <button
          className="mt-16"
          onClick={() => navigate('/tracking?tab=function')}
          style={{ background: 'none', border: 'none', padding: 0, color: 'var(--pa)', fontWeight: 600, fontSize: 14 }}
        >
          See full functional assessment →
        </button>
      </div>

      </>
      )}

      <div style={{ height: 8 }} />

      {callModal && (
        <RequestCallModal plan={callModal} onClose={() => setCallModal(null)} />
      )}
    </div>
  )
}
